import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Contact } from '../../../../../../../../core/models/entities';

export interface ContactForm {
  pk: FormControl<number | null>;
  name: FormControl<string | null>;
  phone: FormControl<string | null>;
  extension: FormControl<string | null>;
  dispatch_line: FormControl<boolean | null>;
  working_days: FormControl<number[] | null>;
  available_from: FormControl<Date | null>;
  available_to: FormControl<Date | null>;
  timezone: FormControl<string | null>;
  organization_id: FormControl<number | null>;
}

@Injectable({
  providedIn: 'root',
})
export class ContactFormService {
  /**
   * E.164 compliant phone number validator
   * Accepts international format with or without formatting characters
   */
  phoneNumberValidator = Validators.pattern('^\\+?[1-9]\\d{1,14}$');

  public createContactForm(contact = new Contact()): FormGroup<ContactForm> {
    return new FormGroup<ContactForm>({
      pk: new FormControl(contact.pk ?? null),
      name: new FormControl(contact.name ?? null, Validators.required),
      phone: new FormControl(contact.phone ?? null, [Validators.required, this.phoneNumberValidator]),
      extension: new FormControl(contact.extension ?? null),
      dispatch_line: new FormControl(contact.dispatch_line ?? false),
      working_days: new FormControl(contact.working_days ?? []),
      available_from: new FormControl(
        contact.available_from ? this.convertTimeStringToDate(contact.available_from) : null
      ),
      available_to: new FormControl(contact.available_to ? this.convertTimeStringToDate(contact.available_to) : null),
      timezone: new FormControl(contact.timezone ?? null),
      organization_id: new FormControl(contact.organization_id ?? null),
    });
  }

  /**
   * Converts a time string (HH:MM) to a Date object
   */
  private convertTimeStringToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  /**
   * Converts dropdown object values to strings for form emission
   * Following Kendo UI best practices for value conversion
   */
  public getDropdownValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return '';
  }

  /**
   * Finds dropdown object from options array based on string value
   */
  public findDropdownObject(options: any[], value: string): any {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  }

  /**
   * Converts multiselect values to string array
   */
  public getMultiselectValues(values: any): string[] {
    if (Array.isArray(values)) {
      return values.map((value) => this.getDropdownValue(value));
    }
    return [];
  }

  /**
   * Finds multiselect objects from options array based on string values
   */
  public findMultiselectObjects(options: any[], values: string[]): any[] {
    if (!Array.isArray(values)) return [];
    return values.map((value) => this.findDropdownObject(options, value)).filter(Boolean);
  }
}
